import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldX, RotateCw } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Skeleton,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { WaapEmptyCard } from '../WaapEmptyCard';
import {
  useWaapVulnerabilities,
  useWaapVulnRules,
  useToggleWaapVulnRule,
  useWaapVulnAssetOptions,
  useWaapVulnAppMapping,
  useWaapVulnAssetMapping,
  useWaapDataLossApiAppOptions,
  useWaapVulnerabilityOverviewCharts,
  API_PROTECTION_TIME_RANGES,
  type ApiProtectionTimeRange,
  type WaapVulnRule,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import { useWaapUpgradeGate } from '@/pages/services/waap/use-waap-upgrade-gate';
import { WaapChartWidget } from '../dashboard/WaapChartWidget';
import styles from './ApiProtectionVulnerabilityPage.module.css';

const PAGE_SIZE = 25;

const TABS = [
  { slug: 'overview', label: 'Overview' },
  { slug: 'vulnerabilities', label: 'Vulnerabilities' },
  { slug: 'rules', label: 'Detection Rules' },
] as const;

const CATEGORIES = ['Data Exposures', 'Password Authentications', 'Access Permissions', 'Security Protocols'];
const TIME_RANGES = [
  { value: 'hour', label: 'Last Hour' },
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: 'lastMonth', label: 'Last Month' },
  { value: 'thisMonth', label: 'This Month' },
];

const riskTone = (v: string) => (v === 'high' ? 'danger' : v === 'medium' ? 'warning' : 'neutral');

function timeWindow(key: string): { begin: number; end: number } {
  const end = Date.now();
  const DAY = 86_400_000;
  const d = new Date();
  switch (key) {
    case 'hour':
      return { begin: end - 3_600_000, end };
    case '7d':
      return { begin: end - 7 * DAY, end };
    case '30d':
      return { begin: end - 30 * DAY, end };
    case 'thisMonth':
      return { begin: new Date(d.getFullYear(), d.getMonth(), 1).getTime(), end };
    case 'lastMonth':
      return {
        begin: new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime(),
        end: new Date(d.getFullYear(), d.getMonth(), 1).getTime(),
      };
    default:
      return { begin: end - DAY, end };
  }
}

const cellOf = (row: Record<string, unknown>, keys: string[]) => {
  for (const k of keys) {
    const v = row[k];
    if (v != null && v !== '') return String(v);
  }
  return '—';
};

/**
 * Overview — native (non-Superset) BI charts for the Vulnerability Detection board
 * (Superset 66), pre-normalized server-side and grouped by kind: scalar KPIs, then
 * the risk-level pie + ranking tables, then the Vulnerability Trend. A user-selectable
 * Time Range drives the dashboard's "time_range" template filter (mirrors the API
 * Protection Management overview).
 */
function OverviewTab({ domainId }: { domainId: string }) {
  const [range, setRange] = useState<ApiProtectionTimeRange>('Last 24 Hours');
  const { data, isLoading, isError, error, refetch, isFetching } = useWaapVulnerabilityOverviewCharts(
    domainId,
    range,
  );
  const charts = data?.charts ?? [];

  return (
    <>
      <div className={styles.toolbar}>
        <span className={styles.rangeLabel}>Time Range</span>
        <Select value={range} onValueChange={(v) => setRange(v as ApiProtectionTimeRange)}>
          <SelectTrigger className={styles.rangeSelect}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {API_PROTECTION_TIME_RANGES.map((r) => (
              <SelectItem key={r} value={r}>
                {r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="secondary"
          onClick={() => refetch()}
          disabled={isFetching}
          className={styles.refreshBtn}
        >
          <RotateCw size={14} />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <Skeleton height={480} />
      ) : data === null ? (
        <WaapEmptyCard
          icon={<ShieldX size={20} />}
          title="Vulnerability analytics not configured"
          description="The analytics board is not set up for this environment yet."
        />
      ) : isError ? (
        <Alert tone="danger" title="Couldn't load Vulnerability overview">
          {errMessage(error)}
        </Alert>
      ) : (
        // Single ordered grid (3 cols): the BE returns the widgets in the vendor's
        // display order and the wide Vulnerability Trend spans 2 columns, so the
        // 8 widgets tile without gaps. dense back-fills any hole at narrower widths.
        <div className={styles.overviewGrid}>
          {charts.map((c) => (
            <div key={c.key} className={c.kind === 'timeseries' ? styles.cellWide : styles.cell}>
              <WaapChartWidget chart={c} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Vulnerabilities — paginated list of detected API vulnerabilities, filtered. */
function VulnerabilitiesTab({ domainId }: { domainId: string }) {
  const [page, setPage] = useState(1);
  const [apiAsset, setApiAsset] = useState('all');
  const [appId, setAppId] = useState('all');
  const [category, setCategory] = useState('all');
  const [range, setRange] = useState('24h');

  const appOpts = useWaapDataLossApiAppOptions(domainId);
  const assetOpts = useWaapVulnAssetOptions(domainId);
  const appMap = useWaapVulnAppMapping(domainId);
  const assetMap = useWaapVulnAssetMapping(domainId);

  const win = useMemo(() => timeWindow(range), [range]);
  const { data, isLoading } = useWaapVulnerabilities(domainId, {
    page,
    length: PAGE_SIZE,
    beginTime: win.begin,
    endTime: win.end,
    appId: appId !== 'all' ? Number(appId) : undefined,
    type: category !== 'all' ? category : undefined,
  });

  const columns = useMemo<ColumnDef<Record<string, unknown>>[]>(
    () => [
      {
        id: 'api_path',
        header: 'API Path',
        cell: ({ row }) => {
          const id = cellOf(row.original, ['api_id', 'apiId', 'asset_id']);
          return assetMap.data?.[id] ?? cellOf(row.original, ['api_path', 'apiPath', 'path']);
        },
      },
      {
        id: 'application',
        header: 'Application',
        cell: ({ row }) => {
          const id = cellOf(row.original, ['app_id', 'appId']);
          return appMap.data?.[id] ?? cellOf(row.original, ['application', 'app_name']);
        },
      },
      { id: 'risk_level', header: 'Risk Level', cell: ({ row }) => cellOf(row.original, ['risk_level', 'level', 'riskLevel']) },
      { id: 'vuln_type', header: 'Vulnerability Type', cell: ({ row }) => cellOf(row.original, ['vulnerability_type', 'name', 'sub_type']) },
      { id: 'vuln_category', header: 'Vulnerability Category', cell: ({ row }) => cellOf(row.original, ['vulnerability_category', 'category', 'type']) },
      { id: 'last_detection', header: 'Last Detection Time', cell: ({ row }) => cellOf(row.original, ['last_detection_time', 'lastDetectionTime', 'updated']) },
      { id: 'requests', header: 'Requests with Vulnerabilities', cell: ({ row }) => cellOf(row.original, ['request_count', 'count', 'requests']) },
    ],
    [appMap.data, assetMap.data],
  );

  const rows = data?.list ?? [];
  const pageCount = Math.max(1, data?.totalPageNumber ?? 1);

  return (
    <Card>
      <CardHeader>
        <CardTitle>API Vulnerabilities</CardTitle>
      </CardHeader>
      <CardBody>
        <div className={styles.filters}>
          {/* API filter: api_asset/options_v2 is empty for now, and the vendor's
              query param for it isn't captured yet, so it's cosmetic (not sent). */}
          <Select value={apiAsset} onValueChange={(v) => { setApiAsset(v); setPage(1); }}>
            <SelectTrigger className={styles.filterSelect}><SelectValue placeholder="API" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All APIs</SelectItem>
              {(assetOpts.data ?? []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={appId} onValueChange={(v) => { setAppId(v); setPage(1); }}>
            <SelectTrigger className={styles.filterSelect}><SelectValue placeholder="Application" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All applications</SelectItem>
              {(appOpts.data ?? []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger className={styles.filterSelect}><SelectValue placeholder="Vulnerability Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={range} onValueChange={(v) => { setRange(v); setPage(1); }}>
            <SelectTrigger className={styles.filterSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <Skeleton height={320} />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            emptyState={
              <EmptyState
                size="sm"
                icon={<ShieldX size={20} />}
                title="No vulnerabilities"
                description="No vulnerabilities match the current filters."
              />
            }
            footer={<Pagination page={page} pageCount={pageCount} onPageChange={setPage} />}
          />
        )}
      </CardBody>
    </Card>
  );
}

/** Detection Rules — managed vulnerability rules (toggle + Edit Whitelist Rule). */
function DetectionRulesTab({ domainId, serviceId }: { domainId: string; serviceId: string }) {
  const { show } = useToast();
  const navigate = useNavigate();
  const { gate, upgradeDialog } = useWaapUpgradeGate();
  const { data, isLoading } = useWaapVulnRules(domainId);
  const toggleRule = useToggleWaapVulnRule(domainId);
  const rules = useMemo(() => [...(data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [data]);
  const toggle = (r: WaapVulnRule) =>
    gate('apiProtection', 'API Protection', () =>
      toggleRule.mutate(
        { id: r.id, body: { ...r, status: r.status === 1 ? 0 : 1 } },
        { onError: (e) => show({ tone: 'danger', title: 'Update failed', description: errMessage(e) }) },
      ),
    )();

  const columns = useMemo<ColumnDef<WaapVulnRule>[]>(
    () => [
      { accessorKey: 'name', header: 'Vulnerability', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name}</span> },
      { id: 'category', header: 'Vulnerability Category', cell: ({ row }) => row.original.config.type },
      { id: 'risk', header: 'Risk Level', cell: ({ row }) => <StatusPill tone={riskTone(row.original.config.level)}>{row.original.config.level}</StatusPill> },
      { id: 'description', header: 'Description', cell: ({ row }) => <span className={styles.muted}>{row.original.config.description}</span> },
      { id: 'suggestion', header: 'Suggestion', cell: ({ row }) => <span className={styles.muted}>{row.original.config.suggestion}</span> },
      { id: 'whitelist', header: 'Whitelist', cell: ({ row }) => row.original.config.whitelist.length },
      { id: 'status', header: 'Status', cell: ({ row }) => <Switch checked={row.original.status === 1} onCheckedChange={() => toggle(row.original)} /> },
      {
        id: 'op',
        header: 'Operation',
        cell: ({ row }) => (
          <Button variant="ghost" size="sm" onClick={() => navigate(`/services/waap/${serviceId}/api-protection/vulnerability/${row.original.id}`)}>
            Edit Whitelist Rule
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [serviceId],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Managed Rules</CardTitle>
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <Skeleton height={240} />
        ) : (
          <DataTable
            columns={columns}
            data={rules}
            emptyState={
              <EmptyState
                size="sm"
                icon={<ShieldX size={20} />}
                title="No managed rules"
                description="No managed vulnerability rules are available for this website."
              />
            }
          />
        )}
      </CardBody>
      {upgradeDialog}
    </Card>
  );
}

/**
 * API Protection → Vulnerability Detection. Overview (native BI charts, board 66),
 * Vulnerabilities (filtered list) and Detection Rules (managed rules, toggle +
 * whitelist editor), backed by the private waap2 portal. Defaults to Overview
 * (matching the vendor); each tab's queries only fire when it's opened.
 */
export function ApiProtectionVulnerabilityPage() {
  // Account-level: any of the account's websites resolves the same customer, so
  // a representative domain is used automatically (no picker / select-website gate).
  const { selected, isLoading: wsLoading } = useWaapWebsite();
  const { serviceId } = useParams<{ serviceId: string }>();
  const domainId = selected?.domainId ?? '';
  const [tab, setTab] = useState<string>('overview');

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'API Protection' }, { label: 'Vulnerability Detection' }]} />}
        title="Vulnerability Detection"
      />

      <Tabs value={tab} onValueChange={setTab} style={{ marginBottom: 'var(--vn-space-4)' }}>
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger key={t.slug} value={t.slug}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {wsLoading ? (
        <Skeleton height={320} />
      ) : tab === 'overview' ? (
        <OverviewTab domainId={domainId} />
      ) : tab === 'vulnerabilities' ? (
        <VulnerabilitiesTab domainId={domainId} />
      ) : (
        <DetectionRulesTab domainId={domainId} serviceId={serviceId ?? ''} />
      )}
    </Container>
  );
}
